// File: frontend/src/pages/AdminPage.tsx
// REVISED: Added Offboarding sub-tab for player management

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/api/auth';
import toast from 'react-hot-toast';
import { useConfirmDialog } from '@/components/common/ConfirmDialog';

// Import API functions from @/api/admin
import {
    fetchUpcomingScheduleAdmin,
    updateKickoffTimeAdmin,
    fetchAdminContests,
    finalizeContestAdmin,
    triggerPlayerSeasonAggregationAdmin,
    triggerTeamDefenseAggregationAdmin,
    triggerTeamOffenseAggregationAdmin,
    triggerLeagueAverageDefenseAggregationAdmin,
    generateDraftRankingsAdmin,
    triggerSyncPlayerLockTimesAdmin,
    triggerResyncContestCompositionsAdmin,
    triggerSalaryCalculationAdmin,
    fetchPlayersWithMissingAttributes,
    updatePlayerAttributes,
    fetchPlayersNeedingOffboarding, // ADD THIS IMPORT
    fetchUsersPendingDeletion,
    purgeUserPersonalData,
    type PendingDeletionUser,
    fetchAdminSeasons,
    type ApiAdminSeasonOption,
} from '@/api/admin';

// Import types/interfaces DIRECTLY from @/api/schemas
import { 
    ApiMatchSchedule,
    ApiDfsContest,
    ApiScoreRecalculationSeasonPayload, 
    ApiAdminTaskResponse,
    ContestIdentifierPayload,
    PlayerRead
} from '@/api/schemas'; 

import { format, parseISO, isValid } from 'date-fns';
import { toZonedTime, format as formatTz } from 'date-fns-tz';
import Spinner from '@/components/common/Spinner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { getAbbreviation } from '@/utils/teams';
import GameLogLeaderboardPage from '@/pages/GameLogLeaderboardPage';
import BlogAdminPanel from '@/components/admin/BlogAdminPanel';
import SeasonDatesPanel from '@/components/admin/SeasonDatesPanel';
import { userFacingError } from '@/utils/userFacingError';

const logger = console;

const getLocalTimeZone = (): string => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch (e) { logger.warn("Could not detect local timezone, falling back to UTC."); return 'UTC'; }
};
type AdminTabKey = 'schedule' | 'contests' | 'aggregation' | 'playerData' | 'gameLogs' | 'blog' | 'deletions' | 'seasonDates';

type EditState = {
  position: string;
  isEligible: boolean;
  status: 'idle' | 'saving' | 'saved' | 'error';
  errorMsg?: string;
};

const PlayerDataManagement: React.FC = () => {
  const { token } = useAuth();
  const showConfirm = useConfirmDialog();
  const queryClient = useQueryClient();

  const [editState, setEditState] = useState<Record<number, EditState>>({});
  
  // === THIS IS THE NEW STATE for managing the sub-tabs ===
  const [activeSubTab, setActiveSubTab] = useState<'onboarding' | 'offboarding'>('onboarding');

  // --- Data Fetching using TanStack Query ---
  const { data: players, isLoading: isLoadingPlayers, error: playersError } = useQuery<PlayerRead[], Error>({
    queryKey: ['unassignedPlayers'],
    queryFn: () => {
      if (!token) return Promise.resolve([]);
      return fetchPlayersWithMissingAttributes(token);
    },
    enabled: !!token,
  });

  // === THIS IS THE NEW useQuery hook for fetching players needing offboarding review ===
  const { 
    data: offboardingPlayers, 
    isLoading: isLoadingOffboarding, 
    error: offboardingError,
  } = useQuery<PlayerRead[], Error>({
    queryKey: ['playersNeedingOffboarding'],
    queryFn: () => {
      if (!token) return Promise.resolve([]);
      return fetchPlayersNeedingOffboarding(token);
    },
    enabled: !!token,
  });

  // --- THIS IS THE UPDATED Mutation with the new payload type ---
  const updatePlayerMutation = useMutation({
    mutationFn: ({ playerId, data }: { 
        playerId: number; 
        data: { 
          // This type exactly matches the backend's Pydantic schema for the PATCH endpoint
          primary_position: 'FW' | 'MF' | 'DF' | 'GK' | null;
        } 
    }) => {
      if (!token) throw new Error("Authentication required");
      return updatePlayerAttributes(token, playerId, data);
    },
    onSuccess: (_, variables) => {
      toast.success(`Player ${variables.playerId} updated successfully!`);
      // Update local state for onboarding view if applicable
      if (variables.playerId in editState) {
        setEditState(prev => ({ ...prev, [variables.playerId]: { ...prev[variables.playerId], status: 'saved' } }));
      }
      // Invalidate both queries to refresh both lists
      queryClient.invalidateQueries({ queryKey: ['unassignedPlayers'] });
      queryClient.invalidateQueries({ queryKey: ['playersNeedingOffboarding'] });
    },
    onError: (error: Error, variables) => {
      toast.error(`Failed to update Player ${variables.playerId}: ${error.message}`);
      // Update local state for onboarding view if applicable
      if (variables.playerId in editState) {
        setEditState(prev => ({ ...prev, [variables.playerId]: { ...prev[variables.playerId], status: 'error', errorMsg: error.message } }));
      }
    },
  });

  useEffect(() => {
    if (players) {
      const newEditState: Record<number, EditState> = {};
      players.forEach(player => {
        newEditState[player.id] = {
          position: player.primary_position || '',
          // The old isEligible state is no longer needed here as the new workflow is position-based
          isEligible: true, // This field is effectively deprecated in this component's state
          status: 'idle',
        };
      });
      setEditState(newEditState);
    }
  }, [players]);

  const handlePositionChange = (playerId: number, newPosition: string) => {
    setEditState(prev => ({ ...prev, [playerId]: { ...prev[playerId], position: newPosition, status: 'idle' } }));
  };

  const handleSave = (playerId: number) => {
    const playerState = editState[playerId];
    if (!playerState.position) {
      toast.error("Please select a position before saving.");
      return;
    }
    setEditState(prev => ({ ...prev, [playerId]: { ...prev[playerId], status: 'saving', errorMsg: undefined } }));
    updatePlayerMutation.mutate({
      playerId,
      data: {
        // Cast the position string to the required literal type for the payload
        primary_position: playerState.position as 'FW' | 'MF' | 'DF' | 'GK',
      },
    });
  };

  return (
    <div className="bg-surface-card p-4 rounded-lg shadow-lg max-w-5xl mx-auto">
      <h2 className="text-2xl font-heading font-bold text-accent-yellow mb-4 text-center">Player Data Management</h2>
      
      {/* Sub-Tab Navigation */}
      <div className="flex border-b border-gray-700 mb-4">
        <button
          onClick={() => setActiveSubTab('onboarding')}
          className={`px-4 py-2 font-semibold text-sm transition-colors ${activeSubTab === 'onboarding' ? 'border-b-2 border-accent-yellow text-accent-yellow' : 'text-gray-400 hover:text-gray-200'}`}
        >
          Onboarding (New Players)
        </button>
        <button
          onClick={() => setActiveSubTab('offboarding')}
          className={`px-4 py-2 font-semibold text-sm transition-colors ${activeSubTab === 'offboarding' ? 'border-b-2 border-accent-yellow text-accent-yellow' : 'text-gray-400 hover:text-gray-200'}`}
        >
          Offboarding (Review Active Players)
        </button>
      </div>

      {/* Onboarding View */}
      {activeSubTab === 'onboarding' && (
        <div>
          <p className="text-center text-gray-400 text-sm mb-4 max-w-2xl mx-auto">
            These players are in a currently supported competition squad but still need a primary fantasy position before they can appear in fantasy games.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm min-w-[700px]">
              <thead className="border-b-2 border-gray-700 text-gray-400 uppercase text-xs tracking-wider">
                <tr>
                  <th className="p-2">Player</th>
                  <th className="p-2">Team</th>
                  <th className="p-2 w-40">Fantasy Position</th>
                  <th className="p-2 w-28 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {isLoadingPlayers && (
                  <tr><td colSpan={4} className="text-center p-8"><Spinner /></td></tr>
                )}
                {playersError && (
                  <tr><td colSpan={4} className="text-center p-8 text-secondary">{playersError.message}</td></tr>
                )}
                {!isLoadingPlayers && players?.length === 0 && (
                  <tr><td colSpan={4} className="text-center p-8 text-gray-500 italic">
                    Every player in the current supported squad pools has an assigned fantasy position.
                  </td></tr>
                )}
                {players?.map(player => {
                  const state = editState[player.id] || { position: '', status: 'idle' };
                  return (
                    <tr key={player.id} className="hover:bg-primary/30 align-middle">
                      <td className="p-2 font-medium">{formatPlayerDisplayName(player)}</td>
                      <td className="p-2">{getAbbreviation(player.current_team_name)}</td>
                      <td className="p-2">
                        <select
                          value={state.position}
                          onChange={(e) => handlePositionChange(player.id, e.target.value)}
                          disabled={state.status === 'saving' || state.status === 'saved'}
                          className="w-full p-1 text-xs rounded bg-primary-dark border border-gray-600"
                        >
                          <option value="">-- Select --</option>
                          <option value="FW">FW</option>
                          <option value="MF">MF</option>
                          <option value="DF">DF</option>
                          <option value="GK">GK</option>
                        </select>
                      </td>
                      <td className="p-2 text-center">
                        <button
                          onClick={() => handleSave(player.id)}
                          disabled={state.status === 'saving' || state.status === 'saved' || !state.position}
                          className="bg-accent-yellow hover:bg-yellow-500 text-primary text-xs font-bold py-1 px-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {state.status === 'saving' && <Spinner size="sm" color="text-primary" />}
                          {state.status === 'saved' && '✓ Saved'}
                          {state.status === 'idle' && 'Save'}
                          {state.status === 'error' && 'Retry'}
                        </button>
                        {state.status === 'error' && <p className="text-xs text-secondary mt-1">{state.errorMsg}</p>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Offboarding View */}
      {activeSubTab === 'offboarding' && (
        <div>
          <p className="text-center text-gray-400 text-sm mb-4 max-w-2xl mx-auto">
            These players are currently eligible but were not found in the latest squad lists. They may have retired or transferred to an unsupported league.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm min-w-[700px]">
              <thead className="border-b-2 border-gray-700 text-gray-400 uppercase text-xs tracking-wider">
                <tr>
                  <th className="p-2">Player</th>
                  <th className="p-2">Team</th>
                  <th className="p-2">Current Position</th>
                  <th className="p-2 w-40 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {isLoadingOffboarding && (
                  <tr><td colSpan={4} className="text-center p-8"><Spinner /></td></tr>
                )}
                {offboardingError && (
                  <tr><td colSpan={4} className="text-center p-8 text-secondary">{offboardingError.message}</td></tr>
                )}
                {!isLoadingOffboarding && offboardingPlayers?.length === 0 && (
                  <tr><td colSpan={4} className="text-center p-8 text-gray-500 italic">
                    No players are currently flagged for review.
                  </td></tr>
                )}
                {offboardingPlayers?.map(player => (
                  <tr key={player.id} className="hover:bg-primary/30 align-middle">
                    <td className="p-2 font-medium">{formatPlayerDisplayName(player)}</td>
                    <td className="p-2">{getAbbreviation(player.current_team_name)}</td>
                    <td className="p-2">{player.primary_position}</td>
                    <td className="p-2 text-center">
                      <button
                        onClick={async () => {
                          if (await showConfirm({ message: `Remove ${formatPlayerDisplayName(player)} from the fantasy player pool?`, confirmLabel: 'Remove', destructive: true })) {
                            updatePlayerMutation.mutate({
                              playerId: player.id,
                              data: { primary_position: null }
                            });
                          }
                        }}
                        disabled={updatePlayerMutation.isPending && updatePlayerMutation.variables?.playerId === player.id}
                        className="bg-secondary hover:bg-red-700 text-white text-xs font-bold py-1 px-2 rounded disabled:opacity-50"
                      >
                        {(updatePlayerMutation.isPending && updatePlayerMutation.variables?.playerId === player.id)
                          ? <Spinner size="sm" />
                          : 'Remove from Pool'
                        }
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

function AdminPage() {
    const { token, user, isLoading: isAuthLoading, isAuthenticated } = useAuth();
    const showConfirm = useConfirmDialog();
    const [activeTab, setActiveTab] = useState<AdminTabKey>('schedule');

    // Schedule State
    const [schedules, setSchedules] = useState<ApiMatchSchedule[]>([]);
    const [isLoadingSchedule, setIsLoadingSchedule] = useState(true);
    const [scheduleError, setScheduleError] = useState<string | null>(null);
    const [daysAhead, setDaysAhead] = useState(7);
    const [editTimes, setEditTimes] = useState<Record<number, string>>({});
    const [updateStatus, setUpdateStatus] = useState<Record<number, 'idle' | 'loading' | 'success' | 'error'>>({});
    const [lastUpdateTime, setLastUpdateTime] = useState<Record<number, number>>({});
    const [localTimeZone, setLocalTimeZone] = useState('UTC');

    // Contest Finalization State
    const [adminContests, setAdminContests] = useState<ApiDfsContest[]>([]);
    const [isLoadingContests, setIsLoadingContests] = useState(true);
    const [contestError, setContestError] = useState<string | null>(null);
    const [contestFilter, setContestFilter] = useState<'all' | 'unfinalized' | 'finalized'>('unfinalized');
    const [finalizingContestId, setFinalizingContestId] = useState<number | null>(null);

    // Data Aggregation Triggers State. One SEASON dropdown drives both values
    // (owner 2026-07-22: free-typing comp id + identifier was error-prone);
    // aggCompId/aggSeasonIdentifier stay as the source of truth the trigger
    // handler already validates.
    const [aggSeasons, setAggSeasons] = useState<ApiAdminSeasonOption[]>([]);
    const [aggCompId, setAggCompId] = useState<string>('');
    const [aggSeasonIdentifier, setAggSeasonIdentifier] = useState<string>('');
    const [isTriggeringPlayerAgg, setIsTriggeringPlayerAgg] = useState<boolean>(false);
    const [isTriggeringTeamAgg, setIsTriggeringTeamAgg] = useState<boolean>(false);
    const [isTriggeringTeamOffenseAgg, setIsTriggeringTeamOffenseAgg] = useState<boolean>(false);
    const [isTriggeringLeagueAvgAgg, setIsTriggeringLeagueAvgAgg] = useState<boolean>(false);
    const [isGeneratingRankings, setIsGeneratingRankings] = useState<boolean>(false);
    const [aggTaskMessage, setAggTaskMessage] = useState<string | null>(null);
    const [aggTaskError, setAggTaskError] = useState<string | null>(null);
    
    // Kickoff Workflow Actions State
    const [isSyncingLockTimes, setIsSyncingLockTimes] = useState<boolean>(false);
    const [syncLockTimesMessage, setSyncLockTimesMessage] = useState<string | null>(null);
    const [syncLockTimesError, setSyncLockTimesError] = useState<string | null>(null);
    const [lockSyncStartDate, setLockSyncStartDate] = useState<string>(''); 
    const [lockSyncEndDate, setLockSyncEndDate] = useState<string>('');   

    const [isResyncingContests, setIsResyncingContests] = useState<boolean>(false);
    const [resyncContestsMessage, setResyncContestsMessage] = useState<string | null>(null);
    const [resyncContestsError, setResyncContestsError] = useState<string | null>(null);
    const [resyncDaysAhead, setResyncDaysAhead] = useState<string>('7');
    const [forceStartTimeUpdate, setForceStartTimeUpdate] = useState<boolean>(false);
    
    // Salary Calculation State
    const [salaryCalcContestId, setSalaryCalcContestId] = useState<string>('');
    const [isCalculatingSalaries, setIsCalculatingSalaries] = useState<boolean>(false);

    useEffect(() => { setLocalTimeZone(getLocalTimeZone()); }, []);

    // Load the season picker once; default to the first active season.
    useEffect(() => {
        if (!token) return;
        fetchAdminSeasons(token)
            .then(seasons => {
                setAggSeasons(seasons);
                const first = seasons.find(s => s.is_active_season) ?? seasons[0];
                if (first) {
                    setAggCompId(String(first.competition_id));
                    setAggSeasonIdentifier(first.season_identifier);
                }
            })
            .catch(() => {
                // Surface it - a silently empty picker leaves the four
                // trigger buttons disabled with no explanation.
                setAggTaskError('Failed to load the seasons list - refresh, or check /admin/seasons.');
            });
    }, [token]);
    
    const loadSchedule = useCallback(async (days: number) => {
        if (!token) { setScheduleError("Authentication required."); setIsLoadingSchedule(false); return; }
        setIsLoadingSchedule(true); setScheduleError(null); setEditTimes({}); setUpdateStatus({});
        try {
            const data = await fetchUpcomingScheduleAdmin(token, days);
            setSchedules(data);
            const initialTimes: Record<number, string> = {};
            const tz = getLocalTimeZone();
            data.forEach((s) => {
                if (s.id && s.kickoff_time_utc) {
                    try {
                        const utcDate = parseISO(s.kickoff_time_utc);
                        if (isValid(utcDate)) {
                            // Check if this is a midnight UTC placeholder (StatsBomb date-only)
                            const isMidnightUtc = utcDate.getUTCHours() === 0 && utcDate.getUTCMinutes() === 0 && utcDate.getUTCSeconds() === 0;
                            if (isMidnightUtc && s.match_date) {
                                // Midnight UTC is a placeholder — use match_date with noon local instead
                                const matchDate = parseISO(s.match_date);
                                if (isValid(matchDate)) {
                                    initialTimes[s.id] = format(new Date(matchDate.getFullYear(), matchDate.getMonth(), matchDate.getDate(), 12, 0, 0), "yyyy-MM-dd'T'HH:mm:ss");
                                }
                            } else {
                                initialTimes[s.id] = format(toZonedTime(utcDate, tz), "yyyy-MM-dd'T'HH:mm:ss");
                            }
                        }
                    } catch (e) { logger.error(`Err processing kickoff ID ${s.id}`, e); }
                } else if (s.id && s.match_date) {
                    try {
                        if (s.match_date) {
                            const matchDate = parseISO(s.match_date);
                            if (isValid(matchDate)) {
                                initialTimes[s.id] = format(new Date(matchDate.getFullYear(), matchDate.getMonth(), matchDate.getDate(), 12, 0, 0), "yyyy-MM-dd'T'HH:mm:ss");
                            }
                        }
                    } catch (e) { logger.error(`Err processing match_date ID ${s.id}`, e); }
                }
            });
            setEditTimes(initialTimes);
        } catch (err: any) { setScheduleError(userFacingError(err, "Failed to load schedule."));
        } finally { setIsLoadingSchedule(false); }
    }, [token]);

    const handleTimeChange = (scheduleId: number, value: string) => {
        setEditTimes(prev => ({ ...prev, [scheduleId]: value }));
        setUpdateStatus(prev => ({ ...prev, [scheduleId]: 'idle' }));
    };

    const handleUpdateTime = async (scheduleId: number) => {
        if (!token) return;
        const localTimeString = editTimes[scheduleId];
        if (!localTimeString || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(localTimeString)) {
            toast.error("Invalid format."); setUpdateStatus(prev => ({ ...prev, [scheduleId]: 'error' })); return;
        }
        setUpdateStatus(prev => ({ ...prev, [scheduleId]: 'loading' })); setScheduleError(null);
        try {
            const dateTimeStr = localTimeString.length === 16 ? `${localTimeString}:00` : localTimeString;
            const localDate = new Date(dateTimeStr);
            if (!isValid(localDate)) throw new Error("Invalid date.");
            await updateKickoffTimeAdmin(token, scheduleId, localDate.toISOString());
            setUpdateStatus(prev => ({ ...prev, [scheduleId]: 'success' }));
            setLastUpdateTime(prev => ({ ...prev, [scheduleId]: Date.now() }));
            setSchedules(prev => prev.map(s => s.id === scheduleId ? { ...s, kickoff_time_utc: localDate.toISOString(), status: 'TimeConfirmed' } : s));
            setEditTimes(prev => ({ ...prev, [scheduleId]: format(toZonedTime(localDate.toISOString(), getLocalTimeZone()), "yyyy-MM-dd'T'HH:mm:ss") }));
        } catch (err: any) {
            const msg = (err instanceof ApiError && err.message) ? err.message : `Failed to update.`;
            setUpdateStatus(prev => ({ ...prev, [scheduleId]: 'error' })); setScheduleError(msg);
        }
    };
    
    const formatDisplayLocalTime = (utcIsoStr: string | null | undefined): string => {
        if (!utcIsoStr) return 'Not Set';
        try { const d = parseISO(utcIsoStr); return isValid(d) ? formatTz(d, 'MMM d, h:mm a (zzz)', { timeZone: localTimeZone }) : 'Invalid Date'; }
        catch (e) { return 'Format Err'; }
    };

    const getStatusIndicator = useCallback((id: number): React.ReactNode => {
        const status = updateStatus[id]; const lastUpd = lastUpdateTime[id];
        if (status === 'loading') return <span className="text-xs text-yellow-400 italic ml-2 animate-pulse">Saving...</span>;
        if (status === 'success' && lastUpd && (Date.now() - lastUpd < 5000)) return <span className="text-xs text-green-400 ml-2">✓ Saved</span>;
        if (status === 'error') return <span className="text-xs text-secondary ml-2">Error!</span>;
        return null;
    }, [updateStatus, lastUpdateTime]);

    const loadAdminContests = useCallback(async () => {
        if (!token) { setContestError("Auth required."); setIsLoadingContests(false); return; }
        setIsLoadingContests(true); setContestError(null);
        try { const data = await fetchAdminContests(token, {}); setAdminContests(data); }
        catch (err:any) { setContestError(userFacingError(err, "Failed to load contests.")); }
        finally { setIsLoadingContests(false); }
    }, [token]);

    const handleFinalizeContest = async (contestId: number) => {
        if (!token || finalizingContestId === contestId) return;
        if (!await showConfirm({ message: `Finalize contest ID ${contestId}?`, confirmLabel: 'Finalize' })) return;
        setFinalizingContestId(contestId); setContestError(null);
        try {
            const updatedContest = await finalizeContestAdmin(token, contestId);
            setAdminContests(prev => prev.map(c => c.id === contestId ? updatedContest : c));
            toast.success(`Contest ${updatedContest.name} finalized.`);
        } catch (err: any) {
            const msg = (err instanceof ApiError && err.message) ? err.message : `Failed to finalize.`;
            setContestError(msg); toast.error(`Error: ${msg}`);
        } finally { setFinalizingContestId(null); }
    };

    const handleTriggerAggregation = async (
        taskName: 'tasks.aggregate_player_season_stats' | 'tasks.aggregate_team_defense_stats' | 'tasks.aggregate_team_offense_stats' | 'tasks.calculate_league_average_defense_stats',
        setLoadingState: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        if (!token) { setAggTaskError("Auth required."); return; }
        // Resolve from state, falling back to the first active season - the
        // click must work even if the dropdown's default-selection effect
        // never landed (owner report 2026-07-22: buttons dead after the
        // dropdown change).
        let effCompId = aggCompId;
        let effIdentifier = aggSeasonIdentifier;
        if ((!effCompId || !effIdentifier.trim()) && aggSeasons.length > 0) {
            const first = aggSeasons.find(s => s.is_active_season) ?? aggSeasons[0];
            effCompId = String(first.competition_id);
            effIdentifier = first.season_identifier;
            setAggCompId(effCompId);
            setAggSeasonIdentifier(effIdentifier);
        }
        const compIdNum = parseInt(effCompId, 10);
        if (isNaN(compIdNum) || compIdNum <= 0) { setAggTaskError("Valid Comp DB ID."); return; }
        if (!effIdentifier.trim()) { setAggTaskError("Season Identifier required."); return; }
        setLoadingState(true); setAggTaskMessage(null); setAggTaskError(null);
        const payload: ApiScoreRecalculationSeasonPayload = {
            target_competition_id: compIdNum,
            target_season_identifier: effIdentifier.trim()
        };
        let apiFunc: (token: string, payload: ApiScoreRecalculationSeasonPayload) => Promise<ApiAdminTaskResponse>;
        if (taskName === 'tasks.aggregate_player_season_stats') apiFunc = triggerPlayerSeasonAggregationAdmin;
        else if (taskName === 'tasks.aggregate_team_defense_stats') apiFunc = triggerTeamDefenseAggregationAdmin;
        else if (taskName === 'tasks.aggregate_team_offense_stats') apiFunc = triggerTeamOffenseAggregationAdmin;
        else if (taskName === 'tasks.calculate_league_average_defense_stats') apiFunc = triggerLeagueAverageDefenseAggregationAdmin;
        else { setAggTaskError("Invalid task."); setLoadingState(false); return; }
        try { const resp = await apiFunc(token, payload); setAggTaskMessage(resp.message + (resp.task_id ? ` (ID: ${resp.task_id})` : ''));
        } catch (err: any) { setAggTaskError(userFacingError(err, `Failed: ${taskName}`));
        } finally { setLoadingState(false); }
    };
    
    // Pre-draft rankings: two-season percentile blend into player_rankings
    // (comp-level; the season dropdown's competition decides which league).
    // ALWAYS previews first (dry run - no writes), shows what would change,
    // and only replaces the live board after an explicit confirm.
    const handleGenerateDraftRankings = async () => {
        if (!token) { setAggTaskError("Auth required."); return; }
        let effCompId = aggCompId;
        if (!effCompId && aggSeasons.length > 0) {
            const first = aggSeasons.find(s => s.is_active_season) ?? aggSeasons[0];
            effCompId = String(first.competition_id);
            setAggCompId(effCompId);
        }
        const compIdNum = parseInt(effCompId, 10);
        if (isNaN(compIdNum) || compIdNum <= 0) { setAggTaskError("Valid Comp DB ID."); return; }
        setIsGeneratingRankings(true); setAggTaskMessage(null); setAggTaskError(null);
        try {
            const dry = await generateDraftRankingsAdmin(token, compIdNum, true);
            if (dry.refused) {
                setAggTaskError(`Refused (nothing written): ${dry.refused}`);
                return;
            }
            const dryTop = dry.preview.slice(0, 5).map((p, i) => `${i + 1}. ${p.name}`).join(', ');
            const confirmed = await showConfirm({
                message:
                    `Generate draft rankings for competition ${compIdNum}?\n\n` +
                    `Would rank ${dry.scored} of ${dry.pool_size} eligible players ` +
                    `(${Math.round(dry.coverage * 100)}% coverage).\n` +
                    `This REPLACES ${dry.existing_rows} existing ranking row(s) - any hand edits are lost.\n\n` +
                    `Top 5: ${dryTop}`,
                confirmLabel: 'Replace rankings',
                destructive: true,
            });
            if (!confirmed) {
                setAggTaskMessage(`Preview only - nothing written. Top 5 would be: ${dryTop}`);
                return;
            }
            const resp = await generateDraftRankingsAdmin(token, compIdNum, false);
            // The write can ALSO refuse (coverage can change between preview
            // and write) - never report a refusal as success.
            if (resp.refused) {
                setAggTaskError(`Refused (nothing written): ${resp.refused}`);
                return;
            }
            // Report the WRITE response's own preview, not the dry run's.
            const writtenTop = resp.preview.slice(0, 5).map((p, i) => `${i + 1}. ${p.name}`).join(', ');
            setAggTaskMessage(
                `Draft rankings: wrote ${resp.written} for comp ${resp.competition_id} ` +
                `(replaced ${resp.existing_rows}). Top 5: ${writtenTop}`
            );
        } catch (err: unknown) {
            setAggTaskError(err instanceof Error ? err.message : 'Failed to generate draft rankings.');
        } finally {
            setIsGeneratingRankings(false);
        }
    };

    const handleTriggerSyncLockTimes = async () => {
        if (!token) { setSyncLockTimesError("Authentication required."); return; }
        setIsSyncingLockTimes(true); setSyncLockTimesMessage(null); setSyncLockTimesError(null);
        try {
            const sDate = lockSyncStartDate.trim() ? lockSyncStartDate.trim() : null;
            const eDate = lockSyncEndDate.trim() ? lockSyncEndDate.trim() : null;
            const response = await triggerSyncPlayerLockTimesAdmin(token, sDate, eDate);
            setSyncLockTimesMessage(response.message + (response.task_id ? ` (Task ID: ${response.task_id})` : ''));
        } catch (err: any) {
            setSyncLockTimesError(userFacingError(err, "Failed to trigger sync player lock times task."));
        } finally {
            setIsSyncingLockTimes(false);
        }
    };

    const handleTriggerResyncContests = async () => {
        if (!token) { setResyncContestsError("Authentication required."); return; }
        setIsResyncingContests(true); setResyncContestsMessage(null); setResyncContestsError(null);
        try {
            const daysParam = resyncDaysAhead.trim() ? parseInt(resyncDaysAhead.trim(), 10) : null;
            if (resyncDaysAhead.trim() && (isNaN(daysParam as number) || (daysParam as number) <= 0)) {
                setResyncContestsError("Days ahead must be a positive number.");
                setIsResyncingContests(false); return;
            }
            const response = await triggerResyncContestCompositionsAdmin(token, daysParam, forceStartTimeUpdate);
            setResyncContestsMessage(response.message + (response.task_id ? ` (Task ID: ${response.task_id})` : ''));
        } catch (err: any) {
            setResyncContestsError(userFacingError(err, "Failed to trigger contest resync task."));
        } finally {
            setIsResyncingContests(false);
        }
    };
    
    const filteredAdminContests = useMemo(() => {
        if (contestFilter === 'all') return adminContests;
        return adminContests.filter(c => c.is_finalized === (contestFilter === 'finalized'));
    }, [adminContests, contestFilter]);

    useEffect(() => {
        if (!isAuthLoading && isAuthenticated) {
            if (activeTab === 'schedule') loadSchedule(daysAhead);
            else if (activeTab === 'contests') loadAdminContests();
            else if (activeTab === 'aggregation') {
                setAggCompId(''); setAggSeasonIdentifier('');
                setAggTaskMessage(null); setAggTaskError(null);
            }
        } else if (!isAuthLoading && !isAuthenticated) {
            const authMsg = "Please log in to access admin features.";
            setScheduleError(authMsg); setContestError(authMsg); setAggTaskError(authMsg);
            setIsLoadingSchedule(false); setIsLoadingContests(false);
        }
    }, [activeTab, daysAhead, loadSchedule, loadAdminContests, isAuthenticated, isAuthLoading]);
    
    const handleTriggerSalaryCalculation = async () => {
        if (!token) {
            toast.error("Authentication required.");
            return;
        }
        const contestIdNum = parseInt(salaryCalcContestId, 10);
        if (isNaN(contestIdNum) || contestIdNum <= 0) {
            toast.error("Please enter a valid Contest ID.");
            return;
        }

        setIsCalculatingSalaries(true);
        const payload: ContestIdentifierPayload = { contest_id: contestIdNum };

        toast.promise(
            triggerSalaryCalculationAdmin(token, payload),
            {
                loading: `Enqueuing salary calculation for Contest ${contestIdNum}...`,
                success: (response) => {
                    setIsCalculatingSalaries(false);
                    return response.message || "Task enqueued successfully!";
                },
                error: (err: any) => {
                    setIsCalculatingSalaries(false);
                    return userFacingError(err, "Failed to enqueue task.");
                },
            }
        );
    };

    if (isAuthLoading) return <div className="flex justify-center items-center min-h-screen bg-primary text-accent-light-blue">Loading Admin Page...</div>;
    if (!isAuthenticated) return <div className="text-center p-6 text-secondary">Please log in to access admin features.</div>;
    if (user && !user.is_superuser) return <div className="text-center p-6 text-secondary">Access Denied: Admin privileges required.</div>;

    return (
    <div className="min-h-screen bg-primary p-4 md:p-8 text-gray-200 font-body">
      <h1 className="text-3xl font-heading font-bold text-accent-light-blue mb-6 text-center">ADMIN PANEL</h1>
      <div className="mb-6 flex justify-center border-b border-gray-700">
        {(['schedule', 'contests', 'aggregation', 'playerData', 'gameLogs', 'blog', 'deletions', 'seasonDates'] as AdminTabKey[]).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-semibold text-sm transition-colors ${
              activeTab === tab ? 'border-b-2 border-accent-yellow text-accent-yellow' : 'text-gray-400 hover:text-accent-light-blue'
            }`}
          >
            {tab === 'playerData' ? 'Player Data' : tab === 'gameLogs' ? 'Game Logs' : tab === 'blog' ? 'Blog' : tab === 'deletions' ? 'Deletions' : tab === 'seasonDates' ? 'Season Dates' : `${tab.charAt(0).toUpperCase() + tab.slice(1)} Management`}
          </button>
        ))}
      </div>

      {activeTab === 'schedule' && (
        <>
          <h2 className="text-2xl font-heading font-bold text-accent-yellow mb-4 text-center">Confirm Match Kickoff Times</h2>
          <p className="text-center text-gray-400 text-sm mb-4 max-w-2xl mx-auto">View upcoming matches. Enter/confirm the kickoff time in your local timezone ({localTimeZone}). The system converts it to UTC. This is crucial for player locking.</p>
          <div className="mb-4 text-center">
            <label htmlFor="days-select" className="mr-2 text-gray-400">Show for next:</label>
            <select id="days-select" value={daysAhead} onChange={(e) => setDaysAhead(parseInt(e.target.value, 10))} className="p-1 rounded bg-primary-dark border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200">
              <option value="3">3 Days</option><option value="7">7 Days</option><option value="14">14 Days</option><option value="21">21 Days</option><option value="30">30 Days</option><option value="45">45 Days</option><option value="60">60 Days</option><option value="90">90 Days</option>
            </select>
            <button onClick={() => loadSchedule(daysAhead)} className="ml-3 bg-secondary hover:bg-red-700 text-white text-sm font-semibold py-1 px-3 rounded transition-colors disabled:opacity-50" disabled={isLoadingSchedule}>{isLoadingSchedule ? 'Loading...' : 'Refresh'}</button>
          </div>
          {scheduleError && <p className="text-center text-secondary mb-4 bg-red-900/30 p-2 rounded">{scheduleError}</p>}
          <div className="bg-surface-card p-4 rounded-lg shadow-lg max-w-5xl mx-auto overflow-x-auto">
            {isLoadingSchedule && schedules.length === 0 ? (<p className="italic p-4 text-center">Loading...</p>
            ) : schedules.length === 0 && !scheduleError ? (<p className="italic p-4 text-center">No matches.</p>
            ) : (
              <table className="w-full text-left text-sm min-w-[700px]">
                <thead><tr className="border-b border-gray-600 text-gray-400 uppercase text-xs tracking-wider"><th className="p-2">Date</th><th className="p-2">Match</th><th className="p-2">Current Kickoff (Local)</th><th className="p-2 w-52">Set Local Kickoff</th><th className="p-2 text-center w-28">Action</th></tr></thead>
                <tbody className="divide-y divide-gray-700">{schedules.map((s) => (<tr key={s.id} className="hover:bg-primary/30 align-middle"><td className="p-2 whitespace-nowrap">{s.match_date ? format(parseISO(s.match_date), 'EEE, MMM d') : 'N/A'}</td><td className="p-2 font-medium">{s.home_team_name_raw || '?'} vs {s.away_team_name_raw || '?'}</td><td className="p-2 whitespace-nowrap text-gray-300 text-xs">{formatDisplayLocalTime(s.kickoff_time_utc)}</td><td className="p-2 w-52"><input type="datetime-local" value={editTimes[s.id] || ''} onChange={(e) => handleTimeChange(s.id, e.target.value)} className="w-full p-1 text-xs rounded bg-primary-dark border border-gray-600" step="1" /></td><td className="p-2 text-center w-28"><button onClick={() => handleUpdateTime(s.id)} disabled={updateStatus[s.id] === 'loading'} className="bg-accent-yellow hover:bg-yellow-500 text-primary text-xs font-bold py-1 px-2 rounded disabled:opacity-50">{updateStatus[s.id] === 'loading' ? <Spinner size="sm" color="text-primary" /> : 'Save'}</button>{getStatusIndicator(s.id)}</td></tr>))}</tbody>
              </table>
            )}
          </div>
          <div className="mt-8 pt-6 border-t border-gray-600 max-w-3xl mx-auto">
            <h3 className="text-xl font-heading font-bold text-accent-light-blue mb-4 text-center">Kickoff Workflow Actions</h3>
            {syncLockTimesMessage && <div className="mb-3 p-2 bg-green-900/50 text-green-300 rounded text-sm">{syncLockTimesMessage}</div>}
            {syncLockTimesError && <div className="mb-3 p-2 bg-red-900/50 text-red-300 rounded text-sm">{syncLockTimesError}</div>}
            {resyncContestsMessage && <div className="mb-3 p-2 bg-green-900/50 text-green-300 rounded text-sm">{resyncContestsMessage}</div>}
            {resyncContestsError && <div className="mb-3 p-2 bg-red-900/50 text-red-300 rounded text-sm">{resyncContestsError}</div>}
            <div className="bg-primary p-4 rounded-lg shadow-md space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Opt. Date Range for Lock Time Sync (YYYY-MM-DD):</label>
                <div className="flex space-x-2">
                  <input type="date" value={lockSyncStartDate} onChange={e => setLockSyncStartDate(e.target.value)} className="w-1/2 p-1 text-xs rounded bg-primary-dark border border-gray-600 text-gray-200" />
                  <input type="date" value={lockSyncEndDate} onChange={e => setLockSyncEndDate(e.target.value)} className="w-1/2 p-1 text-xs rounded bg-primary-dark border border-gray-600 text-gray-200" />
                </div>
                <button onClick={handleTriggerSyncLockTimes} disabled={isSyncingLockTimes} className="mt-2 w-full py-2 px-4 rounded-lg font-semibold text-sm bg-orange-600 hover:bg-orange-500 text-white disabled:opacity-50 transition-colors">
                  {isSyncingLockTimes ? <Spinner size="sm" className="mr-2 inline" /> : null}
                  1. Sync Confirmed Kickoffs to Player Lock Times
                </button>
                <p className="text-xs text-gray-500 mt-1">Updates player lock times based on 'TimeConfirmed' matches. Uses default window if dates blank.</p>
              </div>
              <hr className="border-gray-700" />
              <div>
                <label className="block text-xs text-gray-400 mb-1">Opt. Days Ahead for Contest Resync:</label>
                <input type="number" value={resyncDaysAhead} onChange={e => setResyncDaysAhead(e.target.value)} placeholder="Default (e.g., 7)" className="w-1/2 p-1 text-xs rounded bg-primary-dark border border-gray-600 text-gray-200" />
                <label className="mt-2 flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={forceStartTimeUpdate}
                    onChange={e => setForceStartTimeUpdate(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Force update contest start times
                    <span className="block text-caption text-gray-500 mt-0.5">
                      ⚠ By default, existing contests keep their original <code>start_time</code>. Check this
                      only if a match has been rescheduled and you need <code>start_time</code> to move.
                    </span>
                  </span>
                </label>
                <button onClick={handleTriggerResyncContests} disabled={isResyncingContests} className="mt-2 w-full py-2 px-4 rounded-lg font-semibold text-sm bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-50 transition-colors">
                  {isResyncingContests ? <Spinner size="sm" className="mr-2 inline" /> : null}
                  2. Re-evaluate & Sync Contest Compositions
                </button>
                <p className="text-xs text-gray-500 mt-1">Re-runs contest creation logic using latest kickoff times.</p>
              </div>
            </div>
          </div>
        </>
      )}
      {activeTab === 'contests' && (
        <>
          <h2 className="text-2xl font-heading font-bold text-accent-yellow mb-4 text-center">Manage & Finalize Contests</h2>
          <div className="mb-4 text-center"><label htmlFor="contest-filter-select" className="mr-2 text-gray-400">Filter:</label><select id="contest-filter-select" value={contestFilter} onChange={(e) => setContestFilter(e.target.value as typeof contestFilter)} className="p-1 rounded bg-primary-dark border border-gray-600 text-gray-200"><option value="unfinalized">Unfinalized</option><option value="finalized">Finalized</option><option value="all">All</option></select><button onClick={loadAdminContests} className="ml-3 bg-secondary hover:bg-red-700 text-white text-sm font-semibold py-1 px-3 rounded disabled:opacity-50" disabled={isLoadingContests}>{isLoadingContests ? 'Loading...' : 'Refresh'}</button></div>
          {contestError && <p className="text-center text-secondary mb-4 p-2 rounded">{contestError}</p>}
          <div className="bg-surface-card p-4 rounded-lg shadow-lg max-w-5xl mx-auto overflow-x-auto">
            {isLoadingContests && adminContests.length === 0 ? (<p className="italic p-4 text-center">Loading...</p>
            ) : filteredAdminContests.length === 0 && !contestError ? (<p className="italic p-4 text-center">No contests.</p>
            ) : (<table className="w-full text-left text-sm min-w-[600px]"><thead><tr className="border-b border-gray-600 text-gray-400 uppercase text-xs tracking-wider"><th className="p-2">ID</th><th>Name</th><th>Date</th><th>Start (UTC)</th><th>Status</th><th className="text-center">Action</th></tr></thead><tbody className="divide-y divide-gray-700">{filteredAdminContests.map((c) => (<tr key={c.id} className="hover:bg-primary/30 align-middle"><td className="p-2">{c.id}</td><td className="p-2 font-medium truncate max-w-xs" title={c.name}>{c.name}</td><td className="p-2 whitespace-nowrap">{c.contest_date ? format(parseISO(c.contest_date), 'MMM d, yyyy') : 'N/A'}</td><td className="p-2 whitespace-nowrap text-xs">{c.start_time ? format(parseISO(c.start_time), 'MMM d, HH:mm zzz') : 'Not Set'}</td><td className="p-2"><span className={`px-2 py-0.5 text-badge rounded-full font-semibold ${c.is_finalized ? 'bg-green-700/70 text-green-200' : 'bg-yellow-700/70 text-yellow-200'}`}>{c.is_finalized ? 'Finalized' : 'Pending'}</span></td><td className="p-2 text-center">{!c.is_finalized && (<button onClick={() => handleFinalizeContest(c.id)} disabled={finalizingContestId === c.id} className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold py-1 px-2 rounded disabled:opacity-50">{finalizingContestId === c.id ? <Spinner size="sm" /> : 'Finalize'}</button>)}</td></tr>))}</tbody></table>)}
          </div>
        </>
      )}
      {activeTab === 'aggregation' && (
        <div className="max-w-2xl mx-auto space-y-8">
          <div className="bg-surface-card p-6 rounded-lg shadow-lg">
            <h2 className="text-2xl font-heading font-bold text-accent-yellow mb-4 text-center">Per-Contest Actions</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="salaryCalcContestId" className="block text-sm font-medium text-gray-400 mb-1">
                  Target Contest DB ID:
                </label>
                <input
                  type="number"
                  id="salaryCalcContestId"
                  value={salaryCalcContestId}
                  onChange={(e) => setSalaryCalcContestId(e.target.value)}
                  placeholder="Enter DfsContest ID"
                  className="w-full p-2 rounded bg-primary-dark border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Calculates and updates player salaries for this contest based on projections.
                </p>
              </div>
              <button
                onClick={handleTriggerSalaryCalculation}
                disabled={isCalculatingSalaries || !salaryCalcContestId}
                className="w-full py-2.5 btn-admin-action bg-teal-600 hover:bg-teal-500 disabled:bg-gray-600 text-white"
              >
                {isCalculatingSalaries && <Spinner size="sm" className="mr-2 inline" />}
                Calculate & Update Salaries
              </button>
            </div>
          </div>
          <div className="bg-surface-card p-6 rounded-lg shadow-lg">
            <h2 className="text-2xl font-heading font-bold text-accent-yellow mb-6 text-center">Season-Wide Aggregation</h2>
            <p className="text-sm text-gray-400 mb-6 text-center">Manually trigger Celery tasks to aggregate stats. Run in order after match data processed.</p>
            {aggTaskMessage && <div className="mb-4 p-3 bg-green-900/50 border border-green-500 text-green-300 rounded text-sm">{aggTaskMessage}</div>}
            {aggTaskError && <div className="mb-4 p-3 bg-red-900/50 border border-secondary text-red-300 rounded text-sm">{aggTaskError}</div>}
            <div className="space-y-4 mb-6">
              <div>
                <label htmlFor="aggSeason" className="block text-sm font-medium text-gray-400 mb-1">Season:</label>
                <select
                  id="aggSeason"
                  value={`${aggCompId}|${aggSeasonIdentifier}`}
                  onChange={(e) => {
                    const [cid, ...rest] = e.target.value.split('|');
                    setAggCompId(cid);
                    setAggSeasonIdentifier(rest.join('|'));
                  }}
                  className="w-full p-2 rounded bg-primary-dark border"
                >
                  {/* Placeholder ALWAYS present: if the controlled value ever
                      fails to match a real option, the select shows this
                      instead of silently displaying the first option while
                      the state (and the buttons) think nothing is chosen. */}
                  <option value="|" disabled>
                    {aggSeasons.length === 0 ? 'Loading seasons…' : 'Select a season…'}
                  </option>
                  {aggSeasons.map(s => (
                    <option key={`${s.competition_id}|${s.season_identifier}`} value={`${s.competition_id}|${s.season_identifier}`}>
                      {s.competition_name} — {s.season_identifier}{s.is_active_season ? ' (active)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Selecting a season sets both the competition and identifier for all four actions below.</p>
              </div>
            </div>
            <div className="space-y-3">
              <button onClick={() => handleTriggerAggregation('tasks.aggregate_player_season_stats', setIsTriggeringPlayerAgg)} disabled={isTriggeringPlayerAgg || (!aggCompId && aggSeasons.length === 0)} className="w-full py-2.5 btn-admin-action">{isTriggeringPlayerAgg && <Spinner size="sm" className="mr-2 inline" />}1. Aggregate Player Stats</button>
              <button onClick={() => handleTriggerAggregation('tasks.aggregate_team_defense_stats', setIsTriggeringTeamAgg)} disabled={isTriggeringTeamAgg || (!aggCompId && aggSeasons.length === 0)} className="w-full py-2.5 btn-admin-action">{isTriggeringTeamAgg && <Spinner size="sm" className="mr-2 inline" />}2. Aggregate Team Defense</button>
              <button onClick={() => handleTriggerAggregation('tasks.aggregate_team_offense_stats', setIsTriggeringTeamOffenseAgg)} disabled={isTriggeringTeamOffenseAgg || (!aggCompId && aggSeasons.length === 0)} className="w-full py-2.5 btn-admin-action">{isTriggeringTeamOffenseAgg && <Spinner size="sm" className="mr-2 inline" />}3. Aggregate Team Offense</button>
              <button onClick={() => handleTriggerAggregation('tasks.calculate_league_average_defense_stats', setIsTriggeringLeagueAvgAgg)} disabled={isTriggeringLeagueAvgAgg || (!aggCompId && aggSeasons.length === 0)} className="w-full py-2.5 btn-admin-action">{isTriggeringLeagueAvgAgg && <Spinner size="sm" className="mr-2 inline" />}4. Calculate League Avgs</button>
              <button onClick={handleGenerateDraftRankings} disabled={isGeneratingRankings || (!aggCompId && aggSeasons.length === 0)} className="w-full py-2.5 btn-admin-action" title="Two-season percentile blend (45/30/15/10) into player_rankings - the draft board DR column and autopick tier 1">{isGeneratingRankings && <Spinner size="sm" className="mr-2 inline" />}5. Generate Draft Rankings</button>
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center">Ensure `FantasyScore` data is up-to-date before running.</p>
          </div>
        </div>
      )}
      {activeTab === 'playerData' && (
        <PlayerDataManagement />
      )}
      {activeTab === 'gameLogs' && (
        <GameLogLeaderboardPage />
      )}
      {activeTab === 'blog' && (
        <BlogAdminPanel />
      )}
      {activeTab === 'deletions' && (
        <DeletionsPanel />
      )}
      {activeTab === 'seasonDates' && (
        <SeasonDatesPanel />
      )}
    </div>
  );
}

// --- Account Deletion Requests Panel (stage 2 of the delete flow) ---
// Stage 1: user clicks "Delete My Account" on /profile -> backend sets is_active=False
// and fires a Sentry alert.
// Stage 2 (this panel): admin reviews the list of inactive accounts whose personal
// data has not yet been redacted, and clicks "Purge" to redact username/email/phone/
// google_id/venmo_username on the User row. We pseudonymize in place rather than
// hard-delete because several FK references (team.owner_id, league.owner_id,
// dfs_lineup.user_id) have no ON DELETE rule and would otherwise block deletion
// or cascade-destroy other users' league/contest history.
const DeletionsPanel: React.FC = () => {
  const { token } = useAuth();
  const confirm = useConfirmDialog();
  const queryClient = useQueryClient();

  const { data: pendingUsers, isLoading, error } = useQuery<PendingDeletionUser[], ApiError>({
    queryKey: ['adminPendingDeletions'],
    queryFn: () => fetchUsersPendingDeletion(token!),
    enabled: !!token,
  });

  const purgeMutation = useMutation({
    mutationFn: (userId: number) => purgeUserPersonalData(token!, userId),
    onSuccess: (_data, userId) => {
      toast.success(`User ${userId} purged.`);
      queryClient.invalidateQueries({ queryKey: ['adminPendingDeletions'] });
    },
    onError: (err: any, userId) => {
      const msg = err instanceof ApiError ? err.message : `Failed to purge user ${userId}.`;
      toast.error(msg);
    },
  });

  const handlePurge = async (user: PendingDeletionUser) => {
    const ok = await confirm({
      title: `Purge personal data for user ${user.id}?`,
      message: (
        `This will redact the username (${user.username}), phone number, email, Google ID, ` +
        `and Venmo username on this account. The row stays in the database for historical ` +
        `contest/league integrity but is no longer tied to a person. This action is permanent ` +
        `and cannot be undone.`
      ),
      confirmLabel: 'Purge',
      destructive: true,
    });
    if (ok) purgeMutation.mutate(user.id);
  };

  return (
    <>
      <h2 className="text-2xl font-heading font-bold text-accent-yellow mb-2 text-center">Account Deletion Requests</h2>
      <p className="text-center text-gray-400 text-sm mb-6 max-w-2xl mx-auto">
        Users below have requested account deletion (stage 1: deactivated). Click <strong>Purge</strong> to complete
        stage 2: redact their personal data (username, phone, email, Google ID, Venmo). Per Apple
        Guideline 5.1.1(v) and GDPR Article 17, this must be completed within ~14 days of the request.
      </p>

      {isLoading && <div className="flex justify-center py-8"><Spinner size="lg" /></div>}
      {error && <p className="text-secondary text-center py-4">{error.message}</p>}

      {!isLoading && !error && pendingUsers && pendingUsers.length === 0 && (
        <p className="text-gray-400 text-center py-8 italic">No pending deletion requests.</p>
      )}

      {!isLoading && !error && pendingUsers && pendingUsers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-600 text-gray-400 uppercase text-xs tracking-wider">
                <th className="p-2">ID</th>
                <th className="p-2">Username</th>
                <th className="p-2">Email</th>
                <th className="p-2">Phone</th>
                <th className="p-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {pendingUsers.map((u) => (
                <tr key={u.id} className="hover:bg-primary/30 align-middle">
                  <td className="p-2">{u.id}</td>
                  <td className="p-2 font-medium">{u.username}</td>
                  <td className="p-2 text-gray-400">{u.email ?? '—'}</td>
                  <td className="p-2 text-gray-400">{u.phone_number ?? '—'}</td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() => handlePurge(u)}
                      disabled={purgeMutation.isPending && purgeMutation.variables === u.id}
                      className="bg-secondary hover:bg-red-700 text-white text-xs font-bold py-1 px-3 rounded disabled:opacity-50"
                    >
                      {purgeMutation.isPending && purgeMutation.variables === u.id ? <Spinner size="sm" /> : 'Purge'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default AdminPage;
